import type { PipelineHealthOrganizationBuildingScrape, PipelineHealthStatistic } from '../clients'

export const SOURCE_GRADE_METRIC_WEIGHTS: Partial<Record<keyof PipelineHealthStatistic, number>> = {
  units_with_street_address: 1,
  units_with_city: 1,
  units_with_zip_code: 1,
  units_with_lat: 0,
  units_with_lon: 0,
  units_with_floorplan_name: 1,
  units_with_unit_name: 1,
  units_with_availability: 1,
  units_with_price: 1,
  units_with_bed: 1,
  units_with_bath: 1,
  units_with_sqft: 1,
  units_with_images: 0.5,
  units_with_specials: 0.25,
  units_with_price_plans: 0.75,
  units_with_included_services: 0.25,
  units_with_floor: 0.5,
  units_with_building: 0.25,
  units_with_amenities: 0.25,
  units_with_deposit: 0.25
}

const SOURCE_GRADE_WEIGHT_SUM = Object.values(SOURCE_GRADE_METRIC_WEIGHTS).reduce(
  (sum, weight) => sum + (weight ?? 0),
  0
)

export const computeSourceGrade = (row: PipelineHealthStatistic): number => {
  if (row.units_scraped <= 0) {
    return 0
  }
  let weighted = 0
  for (const [field, weight] of Object.entries(SOURCE_GRADE_METRIC_WEIGHTS) as [
    keyof PipelineHealthStatistic,
    number
  ][]) {
    if (!weight) {
      continue
    }
    const metric = row[field]
    if (metric === undefined) {
      console.error(`Missing pipeline health metric for field ${field}`)
      continue
    }
    weighted += Number(metric) * weight
  }
  return weighted / SOURCE_GRADE_WEIGHT_SUM / row.units_scraped
}

export const sourceGradesBySource = (rows: PipelineHealthStatistic[]): Record<string, number> => {
  const grades: Record<string, number> = {}
  for (const row of rows) {
    grades[row.source] = computeSourceGrade(row)
  }
  return grades
}

/**
 * A day's coverage snapshot is assembled from scrapes finished the day before, so its last_scrape
 * never reaches the snapshot date itself. Comparing against the day before keeps this a freshness
 * check rather than one no building can satisfy.
 */
const previousDay = (dateKey: string): string => {
  const date = new Date(`${dateKey}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) {
    return dateKey
  }
  date.setUTCDate(date.getUTCDate() - 1)
  return date.toISOString().slice(0, 10)
}

export const isScrapedOnOrAfter = (
  building: { last_scrape?: string | null },
  selectedDate: string | null
): boolean => {
  if (selectedDate === null) {
    return false
  }
  if (building.last_scrape === null || building.last_scrape === undefined) {
    return false
  }
  return building.last_scrape >= previousDay(selectedDate)
}

export const isNewListingUrl = (
  building: { listing_url_first_seen?: string | null },
  selectedDate: string | null
): boolean => {
  if (selectedDate === null || !building.listing_url_first_seen) {
    return false
  }
  return (
    building.listing_url_first_seen >= previousDay(selectedDate) &&
    building.listing_url_first_seen <= selectedDate
  )
}

const gradeBucket = (grade: number): 'positive' | 'warning' | 'negative' => {
  if (grade > 0.85) {
    return 'positive'
  }
  if (grade >= 0.7) {
    return 'warning'
  }
  return 'negative'
}

export type OrgBuildingGradeSummary = {
  population: number
  noSource: number
  excluded: number
  excludedSources: string[]
  good: number
  fair: number
  poor: number
  weightedAvg: number | null
  coverage: number
}

/** Unique org-subject buildings scraped on/after selectedDate, plus coverage misses. */
export const summarizeOrgBuildingGrades = (
  buildings: PipelineHealthOrganizationBuildingScrape[],
  sourceGrades: Record<string, number>,
  selectedDate: string | null
): OrgBuildingGradeSummary => {
  const unique = new Map<string, PipelineHealthOrganizationBuildingScrape>()
  const isCoverageMiss = (building: {
    source?: string | null
    last_scrape?: string | null
  }): boolean =>
    !(building.source?.trim() ?? '') &&
    (building.last_scrape === null || building.last_scrape === undefined)
  for (const building of buildings) {
    if (!isScrapedOnOrAfter(building, selectedDate) && !isCoverageMiss(building)) {
      continue
    }
    if (!unique.has(building.building_id)) {
      unique.set(building.building_id, building)
    }
  }

  const orgGradeSummaries: OrgBuildingGradeSummary = {
    population: unique.size,
    noSource: 0,
    excluded: 0,
    excludedSources: [],
    good: 0,
    fair: 0,
    poor: 0,
    weightedAvg: null,
    coverage: 0
  }
  if (orgGradeSummaries.population === 0) {
    return orgGradeSummaries
  }

  const excludedSources = new Set<string>()
  let gradeSum = 0
  let includedInAverage = 0

  for (const building of unique.values()) {
    const source = building.source?.trim() ?? ''
    if (!source) {
      orgGradeSummaries.noSource += 1
      includedInAverage += 1
      continue
    }
    if (!Object.hasOwn(sourceGrades, source)) {
      orgGradeSummaries.excluded += 1
      excludedSources.add(source)
      continue
    }
    const grade = sourceGrades[source]!
    gradeSum += grade
    includedInAverage += 1
    const bucket = gradeBucket(grade)
    if (bucket === 'positive') {
      orgGradeSummaries.good += 1
    } else if (bucket === 'warning') {
      orgGradeSummaries.fair += 1
    } else {
      orgGradeSummaries.poor += 1
    }
  }

  orgGradeSummaries.excludedSources = [...excludedSources].sort()
  orgGradeSummaries.weightedAvg = includedInAverage === 0 ? null : gradeSum / includedInAverage
  orgGradeSummaries.coverage =
    (orgGradeSummaries.population - orgGradeSummaries.noSource - orgGradeSummaries.excluded) /
    orgGradeSummaries.population
  return orgGradeSummaries
}
